/**
 * ESS Climate Intelligence — ENSO Outlook Probability Chart
 * Horizontal stacked bars for 9 upcoming seasons from NOAA CPC
 */

import * as echarts from 'echarts';
import { fetchENSOOutlook, fetchCurrentStatus, seasonName, longDate } from '../data/noaa.js';
import { infoTrigger, infoPanel } from './InfoNote.js';
import { whenInViewport } from '../utils/viewport.js';

export function createENSOOutlook() {
  const section = document.createElement('section');
  section.id = 'enso-outlook';
  section.className = 'section';
  section.innerHTML = `
    <div class="container">
      <div class="section-header anim-on-scroll">
        <div class="section-header__eyebrow">Section 03</div>
        <h2 class="section-header__title">Where Is El Niño Heading? ${infoTrigger('enso-outlook')}</h2>
        <p class="section-header__subtitle">
          Official NOAA CPC consensus probabilities for each of the next nine
          overlapping three-month seasons. Probabilities represent the likelihood
          of El Niño, Neutral, or La Niña conditions. Climate forecasts describe
          probabilities, not guarantees.
        </p>
      </div>

      ${infoPanel('enso-outlook', {
    title: 'Reading This Outlook',
    meaning: 'This graph compares the chances of El Niño, Neutral and La Niña conditions for each coming three-month period. An 80% probability means El Niño is much more likely than the other two states. It does not mean El Niño will occur for 80% of the time.',
    implication: 'Continued El Niño conditions mean that climate-sensitive sectors such as agriculture should continue monitoring rainfall, temperature and water availability. It does not mean that agricultural damage is inevitable.',
    takeaway: 'This is an outlook for the Pacific climate system. Actual agricultural risk must be assessed using observations.',
  })}

      <div id="ensoAdvisory" class="enso-advisory glass-card anim-on-scroll" aria-live="polite" hidden></div>

      <div class="enso-outlook-wrap anim-on-scroll">
        <div class="enso-outlook-chart-wrap glass-card" id="ensoOutlookChartWrap">
          <div class="enso-outlook-loading" id="ensoOutlookLoading">
            <div class="skeleton" style="height:320px;border-radius:var(--radius-md)"></div>
          </div>
          <div id="ensoOutlookChartEl" class="enso-chart-canvas" style="display:none;"></div>
        </div>

        <div class="enso-legend glass-card anim-on-scroll anim-delay-1">
          <div class="enso-legend__item">
            <span class="enso-legend__dot" style="background:#f97316"></span>
            <div>
              <div class="enso-legend__name">El Niño</div>
              <div class="enso-legend__desc">RONI ≥ +0.5°C for ≥3 consecutive months</div>
            </div>
          </div>
          <div class="enso-legend__item">
            <span class="enso-legend__dot" style="background:#64748b"></span>
            <div>
              <div class="enso-legend__name">Neutral</div>
              <div class="enso-legend__desc">RONI between −0.5°C and +0.5°C</div>
            </div>
          </div>
          <div class="enso-legend__item">
            <span class="enso-legend__dot" style="background:#38bdf8"></span>
            <div>
              <div class="enso-legend__name">La Niña</div>
              <div class="enso-legend__desc">RONI ≤ −0.5°C for ≥3 consecutive months</div>
            </div>
          </div>
          <div class="enso-legend__source">
            <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Source: <a href="https://www.cpc.ncep.noaa.gov" target="_blank">NOAA Climate Prediction Center</a>
            · Updated monthly
          </div>
        </div>
      </div>
    </div>
  `;

  injectENSOStyles();
  requestAnimationFrame(() => initENSOChart(section));
  return section;
}

async function initENSOChart(section) {
  const loadingEl = section.querySelector('#ensoOutlookLoading');
  const chartEl = section.querySelector('#ensoOutlookChartEl');

  const [outlook, current] = await Promise.all([fetchENSOOutlook(), fetchCurrentStatus().catch(() => null)]);

  loadingEl.style.display = 'none';
  if (!outlook || !outlook.seasons || !outlook.seasons.length) {
    // No seeded stand-in: say plainly that NOAA's table is not available.
    section.querySelector('#ensoOutlookChartWrap').innerHTML =
      '<p class="enso-unavailable">NOAA\'s official ENSO outlook is temporarily unavailable. This dashboard will update automatically when the next verified table is available.</p>';
    return;
  }
  renderAdvisory(section.querySelector('#ensoAdvisory'), outlook, current);
  const probs = outlook.seasons;
  chartEl.style.display = 'block';

  await whenInViewport(chartEl);

  const chart = echarts.init(chartEl, null, { renderer: 'canvas' });

  const seasons = probs.map(p => p.season);
  const elNinoVals = probs.map(p => p.elNino);
  const neutralVals = probs.map(p => p.neutral);
  const laNinaVals = probs.map(p => p.laNina);

  chart.setOption({
    animation: true,
    animationDuration: 1400,
    animationEasing: 'cubicOut',
    animationDelay: index => index * 65,
    backgroundColor: 'transparent',
    grid: { top: 16, left: 80, right: 80, bottom: 24 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(18,27,53,0.95)',
      borderColor: 'rgba(141,197,61,0.3)',
      borderWidth: 1,
      textStyle: { color: '#f1f5f9', fontFamily: 'Inter, sans-serif', fontSize: 13 },
      formatter(params) {
        const season = params[0].axisValue;
        const rows = params.map(p => {
          const colors = { 'El Niño': '#f97316', 'Neutral': '#64748b', 'La Niña': '#38bdf8' };
          return `<div style="display:flex;align-items:center;gap:8px;margin-top:4px;">
            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${colors[p.seriesName]}"></span>
            <span style="color:#94a3b8">${p.seriesName}</span>
            <span style="margin-left:auto;font-weight:700">${p.value}%</span>
          </div>`;
        }).join('');
        return `<div style="font-weight:600;margin-bottom:4px">${season}</div>${rows}`;
      },
    },
    xAxis: {
      type: 'value',
      max: 100,
      axisLabel: {
        formatter: v => `${v}%`,
        color: 'rgba(255,255,255,0.4)',
        fontSize: 11,
        fontFamily: 'Inter, sans-serif',
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
    },
    yAxis: {
      type: 'category',
      data: seasons,
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: 'rgba(255,255,255,0.75)',
        fontSize: 12,
        fontFamily: 'JetBrains Mono, monospace',
        fontWeight: 'bold',
      },
    },
    series: [
      {
        name: 'El Niño',
        type: 'bar',
        stack: 'probs',
        data: elNinoVals,
        barMaxWidth: 36,
        itemStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
            colorStops: [
              { offset: 0, color: '#f97316' },
              { offset: 1, color: '#fb923c' },
            ],
          },
          borderRadius: [4, 0, 0, 4],
        },
        label: {
          show: true,
          position: 'insideLeft',
          color: 'rgba(255,255,255,0.9)',
          fontSize: 11,
          fontWeight: 'bold',
          fontFamily: 'JetBrains Mono, monospace',
          formatter: p => p.value >= 10 ? `${p.value}%` : '',
        },
      },
      {
        name: 'Neutral',
        type: 'bar',
        stack: 'probs',
        data: neutralVals,
        itemStyle: {
          color: '#334155',
        },
        label: {
          show: true,
          position: 'inside',
          color: 'rgba(255,255,255,0.7)',
          fontSize: 11,
          fontWeight: 'bold',
          fontFamily: 'JetBrains Mono, monospace',
          formatter: p => p.value >= 10 ? `${p.value}%` : '',
        },
      },
      {
        name: 'La Niña',
        type: 'bar',
        stack: 'probs',
        data: laNinaVals,
        itemStyle: {
          color: '#38bdf8',
          borderRadius: [0, 4, 4, 0],
        },
        label: {
          show: true,
          position: 'insideRight',
          color: 'rgba(255,255,255,0.9)',
          fontSize: 11,
          fontWeight: 'bold',
          fontFamily: 'JetBrains Mono, monospace',
          formatter: p => p.value >= 10 ? `${p.value}%` : '',
        },
      },
    ],
  });

  const ro = new ResizeObserver(() => chart.resize());
  ro.observe(chartEl);
  window.addEventListener('resize', () => chart.resize());
}

/** Advisory summary + the key numbers, all taken from NOAA's tables/bulletin (never typed in). */
function renderAdvisory(el, o, current) {
  const sgn = v => (v == null ? 'Not available' : `${v > 0 ? '+' : ''}${Number(v).toFixed(2)}`);
  const pct = v => (v == null ? 'Not available' : `${v}%`);
  const hist = (o.historicEventChance || []).slice().sort((a, b) => b.pct - a.pct)[0];
  const row = hist && o.seasons.find(s => s.season === hist.season);
  const roni = current && current.roni !== 'N/A' ? Number(current.roni) : null;
  const when = hist ? seasonName(hist.season) : null;
  // the RONI value is a 3-month season; the feed carries its middle month (Aug → Jul–Sep)
  const SEAS = ['', 'DJF', 'JFM', 'FMA', 'MAM', 'AMJ', 'MJJ', 'JJA', 'JAS', 'ASO', 'SON', 'OND', 'NDJ'];
  const obsSeason = current && current.updatedDate ? seasonName(`${SEAS[current.updatedDate.getMonth() + 1]} ${current.updatedDate.getFullYear()}`) : null;
  const lead = hist && row
    ? `El Niño is strengthening. According to NOAA's ${longDate(o.issued)} advisory, there is a <strong>${hist.pct}%</strong> probability that the ${when} El Niño will reach or exceed a historic RONI threshold of +2.5°C (stronger than any event in NOAA's record since 1950).`
    : `NOAA's ${longDate(o.issued)} status: <strong>${o.alertStatus || 'unavailable'}</strong>.`;
  el.hidden = false;
  el.innerHTML = `
    <p class="enso-advisory__lead">${lead}</p>
    ${o.synopsis ? `<blockquote class="enso-advisory__quote">${o.synopsis} <cite>NOAA CPC, ${longDate(o.issued)}</cite></blockquote>` : ''}
    ${row ? `<dl class="enso-advisory__grid">
      <div><dt>Observed RONI${obsSeason ? `, ${obsSeason}` : ''}</dt><dd>${sgn(roni)}°C</dd><small>measured, 3-month</small></div>
      <div><dt>Forecast median RONI, ${when}</dt><dd>${sgn(row.roniMedian)}°C</dd><small>90% range ${sgn(row.roniP5)} to ${sgn(row.roniP95)}</small></div>
      <div><dt>El Niño probability, ${when}</dt><dd>${pct(row.elNino)}</dd><small>rounded</small></div>
      <div><dt>Very strong (≥ +2.0°C), ${when}</dt><dd>${pct(row.veryStrong)}</dd><small>rounded</small></div>
      <div><dt>Historic (≥ +2.5°C), ${when}</dt><dd>${pct(hist.pct)}</dd><small>from bulletin</small></div>
    </dl>` : ''}
    <p class="enso-advisory__note">These are forecasts, not observations. 100% and 0% are rounded probabilities, not guarantees. Strength categories use RONI (ERSSTv6-based) throughout; older ONI values are not comparable.</p>`;
}

function injectENSOStyles() {
  if (document.getElementById('enso-styles')) return;
  const style = document.createElement('style');
  style.id = 'enso-styles';
  style.textContent = `
    .enso-advisory { padding: var(--space-5); margin-bottom: var(--space-6); }
    .enso-advisory__lead { font-size: 1.05rem; line-height: 1.55; margin: 0 0 var(--space-3); }
    .enso-advisory__quote { margin: 0 0 var(--space-4); padding-left: var(--space-3); border-left: 3px solid #f97316; color: var(--clr-text-secondary, #cbd5e1); }
    .enso-advisory__quote cite { display: block; font-size: .75rem; opacity: .7; margin-top: 4px; }
    .enso-advisory__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: var(--space-3); margin: 0 0 var(--space-3); }
    .enso-advisory__grid > div { background: rgba(255,255,255,0.04); border-radius: var(--radius-md); padding: var(--space-3); }
    .enso-advisory__grid dt { font-size: .72rem; opacity: .75; }
    .enso-advisory__grid dd { margin: 2px 0; font-size: 1.5rem; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
    .enso-advisory__grid small, .enso-advisory__note { font-size: .72rem; opacity: .7; }
    .enso-unavailable { padding: var(--space-5); margin: 0; }
    #enso-outlook {
      background: linear-gradient(180deg, var(--clr-deep) 0%, var(--clr-void) 100%);
    }

    .enso-outlook-wrap {
      display: grid;
      grid-template-columns: 1fr 280px;
      gap: var(--space-6);
      align-items: start;
    }

    .enso-chart-canvas {
      width: 100%;
      height: 340px;
    }

    .enso-outlook-chart-wrap {
      padding: var(--space-6);
    }

    .enso-legend {
      padding: var(--space-5);
      display: flex;
      flex-direction: column;
      gap: var(--space-4);
    }

    .enso-legend__item {
      display: flex;
      gap: var(--space-3);
      align-items: flex-start;
    }

    .enso-legend__dot {
      width: 12px;
      height: 12px;
      border-radius: 2px;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .enso-legend__name {
      font-weight: var(--weight-semi);
      font-size: var(--text-sm);
      margin-bottom: 2px;
    }

    .enso-legend__desc {
      font-size: var(--text-xs);
      color: var(--clr-text-tertiary);
      line-height: 1.4;
    }

    .enso-legend__source {
      font-size: var(--text-xs);
      color: var(--clr-text-tertiary);
      padding-top: var(--space-4);
      border-top: 1px solid var(--clr-border);
      display: flex;
      align-items: flex-start;
      gap: var(--space-2);
      line-height: 1.5;
    }

    .enso-legend__source a { color: var(--clr-accent); }
    .enso-legend__source a:hover { text-decoration: underline; }

    @media (max-width: 768px) {
      .enso-outlook-wrap { grid-template-columns: 1fr; }
      .enso-chart-canvas { height: 300px; }
    }
    @media (max-width: 480px) {
      .enso-chart-canvas { height: 260px; }
    }
  `;
  document.head.appendChild(style);
}
